// CSV lesen und schreiben (Komma oder Semikolon, Anführungszeichen, UTF-8 oder Latin-1) und die Spaltenzuordnung
// für den Lead-Import. Reine Funktionen, ohne Oberfläche.

export interface CsvInhalt {
  kopf: string[];
  zeilen: string[][];
  trenner: "," | ";" | "\t";
  kodierung: "utf-8" | "windows-1252";
}

/** Bytes als Text: UTF-8, sonst Windows-1252 (Latin-1 aus Excel). Ein BOM wird entfernt. */
export function dekodieren(bytes: ArrayBuffer): { text: string; kodierung: "utf-8" | "windows-1252" } {
  try {
    const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return { text: text.replace(/^﻿/, ""), kodierung: "utf-8" };
  } catch {
    return { text: new TextDecoder("windows-1252").decode(bytes), kodierung: "windows-1252" };
  }
}

function trennerErkennen(text: string): "," | ";" | "\t" {
  // erste Zeile außerhalb von Anführungszeichen auszählen
  const zahl = { ",": 0, ";": 0, "\t": 0 };
  let in_ = false;
  for (const z of text) {
    if (z === '"') in_ = !in_;
    else if (!in_ && z === "\n") break;
    else if (!in_ && (z === "," || z === ";" || z === "\t")) zahl[z]++;
  }
  const sortiert = (Object.entries(zahl) as [",", number][]).sort((a, b) => b[1] - a[1]);
  return sortiert[0][1] > 0 ? (sortiert[0][0] as "," | ";" | "\t") : ",";
}

export function csvLesen(text: string, kodierung: "utf-8" | "windows-1252" = "utf-8"): CsvInhalt {
  const trenner = trennerErkennen(text);
  const zeilen: string[][] = [];
  let zeile: string[] = [];
  let feld = "";
  let in_ = false;
  for (let i = 0; i < text.length; i++) {
    const z = text[i];
    if (in_) {
      if (z === '"') {
        if (text[i + 1] === '"') {
          feld += '"';
          i++;
        } else in_ = false;
      } else feld += z;
    } else if (z === '"') in_ = true;
    else if (z === trenner) {
      zeile.push(feld);
      feld = "";
    } else if (z === "\n" || z === "\r") {
      if (z === "\r" && text[i + 1] === "\n") i++;
      zeile.push(feld);
      feld = "";
      zeilen.push(zeile);
      zeile = [];
    } else feld += z;
  }
  if (feld !== "" || zeile.length) {
    zeile.push(feld);
    zeilen.push(zeile);
  }
  const nichtLeer = zeilen.filter((r) => r.some((c) => c.trim() !== ""));
  const kopf = (nichtLeer[0] ?? []).map((k) => k.trim());
  return { kopf, zeilen: nichtLeer.slice(1), trenner, kodierung };
}

/** CSV mit Semikolon (deutsches Excel), Zellen mit Trenner/Anführungszeichen/Zeilenumbruch in Anführungszeichen. */
export function csvSchreiben(zeilen: string[][]): string {
  return zeilen.map((r) => r.map((c) => (/[";\r\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(";")).join("\r\n");
}

/** Datei speichern (mit BOM, damit Excel UTF-8 erkennt). */
export function dateiSpeichern(name: string, inhalt: string, typ = "text/csv;charset=utf-8"): void {
  const blob = new Blob(["﻿", inhalt], { type: typ });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// ---------------------------------------------------------------------------
// Spaltenzuordnung
// ---------------------------------------------------------------------------

export type ZielFeld =
  | "firma"
  | "kontakt_name"
  | "telefon"
  | "email"
  | "website"
  | "strasse"
  | "plz"
  | "ort"
  | "land"
  | "status"
  | "beschreibung";

export const ZIELFELDER: { key: ZielFeld; label: string; erkennen: RegExp }[] = [
  { key: "firma", label: "Firma", erkennen: /^(firma|firmenname|unternehmen|company|name|lead|lead[ _-]?name|betrieb)$/i },
  { key: "kontakt_name", label: "Kontakt", erkennen: /^(kontakt|kontaktname|ansprechpartner|ansprechperson|contact|contact[ _-]?name|inhaber|geschäftsführer|geschaeftsfuehrer)$/i },
  { key: "telefon", label: "Telefon", erkennen: /^(telefon|tel|tel\.|telefonnummer|phone|mobil|handy|rufnummer)$/i },
  { key: "email", label: "E-Mail", erkennen: /^(e-?mail|mail|e-?mail[ _-]?adresse|email[ _-]?address)$/i },
  { key: "website", label: "Website", erkennen: /^(website|webseite|url|homepage|internet|web)$/i },
  { key: "strasse", label: "Straße", erkennen: /^(stra(ß|ss)e|adresse|address|street|anschrift)$/i },
  { key: "plz", label: "PLZ", erkennen: /^(plz|postleitzahl|zip|zipcode|zip[ _-]?code)$/i },
  { key: "ort", label: "Ort", erkennen: /^(ort|stadt|city|wohnort)$/i },
  { key: "land", label: "Land", erkennen: /^(land|country)$/i },
  { key: "status", label: "Status", erkennen: /^(status|lead[ _-]?status)$/i },
  { key: "beschreibung", label: "Beschreibung", erkennen: /^(beschreibung|notiz|notizen|description|bemerkung)$/i },
];

/** Zuordnung pro CSV-Spalte: Zielfeld, `custom:cf_…` oder null (ignorieren). */
export type Zuordnung = (ZielFeld | `custom:${string}` | null)[];

export function zuordnungVorschlagen(kopf: string[], customFelder: { id: string; name: string }[]): Zuordnung {
  const belegt = new Set<string>();
  return kopf.map((spalte) => {
    const name = spalte.trim();
    const standard = ZIELFELDER.find((z) => !belegt.has(z.key) && z.erkennen.test(name));
    if (standard) {
      belegt.add(standard.key);
      return standard.key;
    }
    const cf = customFelder.find((f) => !belegt.has(`custom:${f.id}`) && f.name.trim().toLocaleLowerCase("de") === name.toLocaleLowerCase("de"));
    if (cf) {
      belegt.add(`custom:${cf.id}`);
      return `custom:${cf.id}` as const;
    }
    return null;
  });
}

export interface ZeilenWerte {
  firma?: string;
  kontakt_name?: string;
  telefon?: string;
  email?: string;
  website?: string;
  strasse?: string;
  plz?: string;
  ort?: string;
  land?: string;
  status?: string;
  beschreibung?: string;
  custom?: Record<string, string>;
}

export function zeileZuordnen(zeile: string[], zuordnung: Zuordnung): ZeilenWerte {
  const aus: ZeilenWerte = {};
  zuordnung.forEach((ziel, i) => {
    const wert = (zeile[i] ?? "").trim();
    if (!ziel || !wert) return;
    if (ziel.startsWith("custom:")) {
      aus.custom = { ...(aus.custom ?? {}), [ziel.slice(7)]: wert };
    } else {
      const k = ziel as ZielFeld;
      // Mehrere Spalten auf dasselbe Feld: Werte hintereinander (z. B. zwei Telefonspalten -> erste gilt)
      aus[k] = aus[k] ? aus[k] : wert;
    }
  });
  return aus;
}
